import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  Copy,
  ExternalLink,
  FileWarning,
  Globe2,
  Languages,
  Link2,
  ListChecks,
  Mail,
  MousePointer2,
  RefreshCw,
  Search,
  ShieldCheck,
  Split,
  XCircle,
  Zap
} from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { LinkAudit, LinkItem, LinkLevel } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type Filter = "all" | "fail" | "warn" | "pass" | "skipped";

const text = {
  en: {
    product: "LinkPulse Scout",
    title: "Broken links before they ship",
    subtitle: "Scan one public page for failed URLs, redirects, skipped links, and weak anchor text.",
    url: "Public page URL",
    scan: "Scan page",
    scanning: "Scanning",
    sample: "Sample",
    scope: "Single-page QA. Public HTTP(S) links only. Private hosts are rejected.",
    score: "Link score",
    overview: "Scan overview",
    issues: "Priority fixes",
    table: "Link evidence",
    report: "Copy-ready report",
    all: "All",
    failed: "Failed",
    warnings: "Warnings",
    passed: "Passed",
    skipped: "Skipped",
    copy: "Copy",
    copied: "Copied",
    empty: "No links match this filter.",
    noIssues: "No priority link fixes found.",
    imageAlt: "Abstract broken link and redirect checker board",
    error: "The page scan could not run.",
    official: "Reference",
    google: "Google link guidance",
    w3c: "W3C Link Checker"
  },
  ko: {
    product: "LinkPulse Scout",
    title: "배포 전 깨진 링크 검사",
    subtitle: "공개 페이지 하나의 실패 URL, 리다이렉트, 제외 링크, 약한 앵커 텍스트를 검사합니다.",
    url: "공개 페이지 URL",
    scan: "페이지 검사",
    scanning: "검사 중",
    sample: "샘플",
    scope: "단일 페이지 QA입니다. 공개 HTTP(S) 링크만 검사하고 내부 호스트는 거부합니다.",
    score: "링크 점수",
    overview: "검사 요약",
    issues: "우선 수정",
    table: "링크 증거",
    report: "복사용 리포트",
    all: "전체",
    failed: "실패",
    warnings: "경고",
    passed: "정상",
    skipped: "제외",
    copy: "복사",
    copied: "복사됨",
    empty: "이 필터에 맞는 링크가 없습니다.",
    noIssues: "우선 수정할 링크가 없습니다.",
    imageAlt: "깨진 링크와 리다이렉트 검사 보드",
    error: "페이지 검사를 실행하지 못했습니다.",
    official: "참고",
    google: "Google 링크 가이드",
    w3c: "W3C 링크 검사기"
  }
};

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [targetUrl, setTargetUrl] = useState("https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Status/404");
  const [audit, setAudit] = useState<LinkAudit>(sampleAudit);
  const [filter, setFilter] = useState<Filter>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const t = text[language];

  const filteredLinks = useMemo(() => {
    if (filter === "all") return audit.links;
    if (filter === "skipped") {
      return audit.links.filter((link) => !["internal", "external"].includes(link.kind));
    }
    return audit.links.filter((link) => link.level === filter);
  }, [audit.links, filter]);

  async function runScan(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ url: targetUrl });
      const response = await fetch(`/api/scan?${params.toString()}`);
      const payload = (await response.json()) as LinkAudit | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? t.error : t.error);
      }
      const nextAudit = payload as LinkAudit;
      setAudit(nextAudit);
      setFilter(nextAudit.summary.failures ? "fail" : nextAudit.summary.warnings ? "warn" : "all");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="hero-band">
        <header className="topbar">
          <a data-google-vignette="false" className="brand" href="/" aria-label="LinkPulse Scout home">
            <span className="brand-mark">
              <Link2 size={22} />
            </span>
            <span>{t.product}</span>
          </a>
          <nav aria-label="Site links">
            <a data-google-vignette="false" href="/about.html">About</a>
            <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          </nav>
          <button className="language-button" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language.toUpperCase()}
          </button>
        </header>

        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">
              <Zap size={16} />
              Single Page Link QA
            </p>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
            <form className="scan-form" onSubmit={runScan}>
              <label>
                <span>{t.url}</span>
                <div className="input-shell">
                  <Globe2 size={18} />
                  <input value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} spellCheck={false} />
                </div>
              </label>
              <div className="action-row">
                <button className="secondary-button" type="button" onClick={() => setAudit(sampleAudit)}>
                  <Clipboard size={17} />
                  {t.sample}
                </button>
                <button className="primary-button" type="submit" disabled={loading}>
                  {loading ? <RefreshCw className="spin" size={17} /> : <Search size={17} />}
                  {loading ? t.scanning : t.scan}
                </button>
              </div>
            </form>
            <p className="scope-line">
              <ShieldCheck size={16} />
              {t.scope}
            </p>
          </div>
          <figure className="hero-visual">
            <img src="/linkpulse-board.png" alt={t.imageAlt} />
          </figure>
        </section>
      </section>

      {error ? (
        <div className="error-banner" role="alert">
          <AlertTriangle size={18} />
          {error}
        </div>
      ) : null}

      <section className="summary-grid" aria-label={t.overview}>
        <ScoreCard audit={audit} label={t.score} />
        <MetricCard icon={<ListChecks size={18} />} label="Checked" value={String(audit.summary.checked)} detail={`${audit.summary.total} total links`} level="info" />
        <MetricCard icon={<XCircle size={18} />} label="Failed" value={String(audit.summary.failures)} detail="4xx, 5xx, network errors" level={audit.summary.failures ? "fail" : "pass"} />
        <MetricCard icon={<Split size={18} />} label="Redirects" value={String(audit.links.filter((link) => link.redirected).length)} detail="Links to update directly" level={audit.links.some((link) => link.redirected) ? "warn" : "pass"} />
        <MetricCard icon={<MousePointer2 size={18} />} label="Anchors" value={String(audit.links.filter((link) => link.issue.includes("anchor")).length)} detail="Weak or empty labels" level={audit.links.some((link) => link.issue.includes("anchor")) ? "warn" : "pass"} />
      </section>

      <section className="workspace">
        <div className="primary-column">
          <Panel title={t.table} icon={<Link2 size={18} />}>
            <div className="filter-row" role="tablist" aria-label="Link filters">
              <FilterButton label={t.all} value="all" active={filter} setFilter={setFilter} count={audit.summary.total} />
              <FilterButton label={t.failed} value="fail" active={filter} setFilter={setFilter} count={audit.summary.failures} />
              <FilterButton label={t.warnings} value="warn" active={filter} setFilter={setFilter} count={audit.summary.warnings} />
              <FilterButton label={t.passed} value="pass" active={filter} setFilter={setFilter} count={audit.summary.passed} />
              <FilterButton label={t.skipped} value="skipped" active={filter} setFilter={setFilter} count={audit.summary.skipped} />
            </div>
            <LinkTable links={filteredLinks} emptyText={t.empty} />
          </Panel>
        </div>

        <aside className="side-column">
          <Panel title={t.issues} icon={<FileWarning size={18} />}>
            <IssueList issues={audit.issues} emptyText={t.noIssues} />
          </Panel>
          <Panel title={t.report} icon={<Clipboard size={18} />}>
            <div className="code-box">
              <pre>{audit.report}</pre>
              <CopyButton value={audit.report} copyLabel={t.copy} copiedLabel={t.copied} />
            </div>
          </Panel>
          <aside className="reference-card">
            <strong>{t.official}</strong>
            <a data-google-vignette="false" href="https://developers.google.com/search/docs/crawling-indexing/links-crawlable" target="_blank" rel="noreferrer">
              {t.google}
              <ExternalLink size={14} />
            </a>
            <a data-google-vignette="false" href="https://validator.w3.org/checklink" target="_blank" rel="noreferrer">
              {t.w3c}
              <ExternalLink size={14} />
            </a>
          </aside>
        </aside>
      </section>
    </main>
  );
}

function ScoreCard({ audit, label }: { audit: LinkAudit; label: string }) {
  const angle = Math.max(0, Math.min(100, audit.score)) * 3.6;
  return (
    <article className="score-card">
      <div className="score-ring" style={{ "--angle": `${angle}deg` } as React.CSSProperties}>
        <span>{audit.score}</span>
      </div>
      <div>
        <p>{label}</p>
        <strong>{audit.scoreLabel}</strong>
        <span>{audit.summary.passed} pass / {audit.summary.warnings} warn / {audit.summary.failures} fail</span>
      </div>
    </article>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  level
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  level: LinkLevel;
}) {
  return (
    <article className={`metric-card ${level}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <span>{detail}</span>
      </div>
    </article>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <header>
        <div>
          {icon}
          <h2>{title}</h2>
        </div>
      </header>
      {children}
    </section>
  );
}

function FilterButton({
  label,
  value,
  active,
  setFilter,
  count
}: {
  label: string;
  value: Filter;
  active: Filter;
  setFilter: (value: Filter) => void;
  count: number;
}) {
  return (
    <button type="button" className={active === value ? "active" : ""} onClick={() => setFilter(value)}>
      <span>{label}</span>
      <strong>{count}</strong>
    </button>
  );
}

function LinkTable({ links, emptyText }: { links: LinkItem[]; emptyText: string }) {
  if (!links.length) {
    return (
      <div className="empty-state">
        <CheckCircle2 size={18} />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="link-table" role="table" aria-label="Scanned links">
      <div className="table-head" role="row">
        <span>State</span>
        <span>Anchor</span>
        <span>URL</span>
        <span>Status</span>
        <span>Fix</span>
      </div>
      {links.map((link) => (
        <article className={`link-row ${link.level}`} role="row" key={`${link.id}-${link.normalizedUrl}`}>
          <div className="state-cell">
            <StatusIcon level={link.level} />
            <KindBadge link={link} />
          </div>
          <div className="anchor-cell">
            <strong>{link.anchor}</strong>
            <span>#{link.sourceIndex}</span>
          </div>
          <div className="url-cell">
            <span>{link.normalizedUrl}</span>
            {link.redirected ? <em>{link.redirectChain.join(" -> ")}</em> : null}
          </div>
          <div className="status-cell">
            <strong>{link.statusText}</strong>
            {link.status ? <span>{link.status}</span> : null}
          </div>
          <div className="fix-cell">{link.issue || "No action"}</div>
        </article>
      ))}
    </div>
  );
}

function KindBadge({ link }: { link: LinkItem }) {
  const icon = link.kind === "mail" ? <Mail size={13} /> : <Link2 size={13} />;
  return (
    <span className={`kind-badge ${link.kind}`}>
      {icon}
      {link.kind}
    </span>
  );
}

function IssueList({ issues, emptyText }: { issues: string[]; emptyText: string }) {
  if (!issues.length) {
    return (
      <div className="empty-state">
        <CheckCircle2 size={18} />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="issue-list">
      {issues.map((issue, index) => (
        <article key={issue}>
          <span>{index + 1}</span>
          <p>{issue}</p>
        </article>
      ))}
    </div>
  );
}

function StatusIcon({ level }: { level: LinkLevel }) {
  if (level === "pass") return <CheckCircle2 className="level pass" size={18} />;
  if (level === "fail") return <XCircle className="level fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="level warn" size={18} />;
  return <ShieldCheck className="level info" size={18} />;
}

function CopyButton({ value, copyLabel, copiedLabel }: { value: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  }

  return (
    <button className="copy-button" type="button" onClick={copy}>
      <Copy size={15} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
